import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import PageError from '@aurora/shared-client/components/error/PageError/PageError';
import useSeoProperties from '@aurora/shared-client/components/seo/useSeoProperties';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import useIsomorphicRedirect, {
  RedirectStatusCode
} from '@aurora/shared-client/components/useIsomorphicRedirect';
import { EndUserComponent, EndUserPages } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import React, { useContext } from 'react';
import type { BreadcrumbItem } from '../../../components/context/BreadcrumbContext/BreadcrumbContext';
import BasePage from '../../../components/page/BasePage/BasePage';
import {
  shouldRedirectUserPage,
  getRedirectRulesForUserPage
} from '../../../components/users/UserHelper';
import useTranslation from '../../../components/useTranslation';

const UserProfileActionMenu = dynamic(
  () => import('../../../components/users/UserProfileActionMenu/UserProfileActionMenu')
);

/**
 * User Page
 *
 * @author Adam Ayres
 */
const UserPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { community, contextUser: user } = useContext(AppContext);
  const { getCaseSensitivePath } = useSeoProperties();
  const { formatMessage, loading: textLoading } = useTranslation(EndUserComponent.USER_PAGE);
  const { router } = useEndUserRoutes();

  // to redirect user profile page when username is invalid
  useIsomorphicRedirect(
    shouldRedirectUserPage(user, router, getCaseSensitivePath),
    getRedirectRulesForUserPage(user, router, getCaseSensitivePath),
    RedirectStatusCode.MOVED_PERMANENTLY
  );

  if (!user) {
    return <PageError statusCode={404} title={formatMessage('invalidUser')} />;
  }

  const title = formatMessage('title', {
    communityTitle: community.title,
    userLogin: user.login
  });

  function decorateBreadcrumbs(breadcrumbs: BreadcrumbItem[]): BreadcrumbItem[] {
    return [
      ...breadcrumbs,
      {
        id: user.id,
        title: user.login
      }
    ];
  }

  function renderActionMenu(): React.ReactElement {
    return <UserProfileActionMenu contextUser={user} />;
  }

  return (
    <BasePage
      pageId={EndUserPages.UserPage}
      nextSeoProps={{ title, noindex: true, nofollow: true }}
      decorateBreadcrumbs={decorateBreadcrumbs}
      breadcrumbActionComponents={[renderActionMenu]}
      showLoading={textLoading}
    />
  );
};

export default UserPage;
